import { webApi } from '@/shared/api/WebApi';
import { ApiError } from '@/shared/api/ApiError';
import type { IListRes } from '@lib2/model/common/ListRes';
import type { ListlistDto, ListlistOrderedNode, OrderedNodesResult } from '@lib2/model/listlist/ListlistDto';
import type {
  ListlistCreateReq,
  ListlistUpdateReq,
  ListlistCreatePositionReq,
  ListlistDeletePositionReq,
  ListlistUpdatePositionsReq,
} from '@lib2/model/listlist/ListlistReq';

export class AppContentApi {
  // ── Listlist CRUD ──

  static async getListlists(cId: number): Promise<IListRes<ListlistDto>> {
    return webApi.get<IListRes<ListlistDto>>('listlist', { cId });
  }

  /** Not-found/cross-account arrives as an empty 200 — normalized to a 404. */
  static async getListlist(cId: number, llId: number): Promise<ListlistDto> {
    const res = await webApi.get<ListlistDto>(`listlist/${llId}`, { cId });
    return ApiError.requireFound(res, 'llr', 'Content list');
  }

  static async createListlist(cId: number, req: ListlistCreateReq): Promise<ListlistDto> {
    return webApi.post<ListlistDto>('listlist', { cId }, req);
  }

  static async updateListlist(
    cId: number,
    llId: number,
    req: ListlistUpdateReq,
  ): Promise<ListlistDto> {
    return webApi.post<ListlistDto>(`listlist/${llId}`, { cId }, req);
  }

  static async deleteListlist(cId: number, llId: number): Promise<void> {
    return webApi.delete<void>(`listlist/${llId}`, { cId });
  }

  // ── Positions / Hierarchy ──

  static async getPositions(cId: number): Promise<OrderedNodesResult> {
    return webApi.get<OrderedNodesResult>('listlist/positions', { cId });
  }

  static async getPositionsByRoot(
    cId: number,
    rootLlId: number,
  ): Promise<ListlistOrderedNode> {
    return webApi.get<ListlistOrderedNode>(`listlist/positions/${rootLlId}`, { cId });
  }

  static async createPosition(cId: number, req: ListlistCreatePositionReq): Promise<void> {
    return webApi.post<void>('listlist/positions', { cId }, req);
  }

  static async deletePosition(cId: number, req: ListlistDeletePositionReq): Promise<void> {
    return webApi.post<void>('listlist/delete-position', { cId }, req);
  }

  static async updatePositions(
    cId: number,
    req: ListlistUpdatePositionsReq,
  ): Promise<void> {
    return webApi.post<void>('listlist/update-positions', { cId }, req);
  }
}
